import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const SimpleLoginForm = () => {
  const navigate = useNavigate();
  const { signIn, resendConfirmationEmail } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendStatus, setResendStatus] = useState('');

  const handleChange = (e) => {
    if (!e || typeof e !== 'object') {
      console.error('Invalid event object received in handleChange');
      return;
    }

    const target = e?.target || e?.currentTarget;
    if (!target) {
      console.error('Event target is undefined');
      return;
    }
    
    const { name, value } = target;
    
    if (!name) {
      console.error('Input element missing name attribute');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors?.[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    if (e && typeof e?.preventDefault === 'function') {
      e?.preventDefault();
    }

    if (!validateForm()) return;

    // Safety check to ensure signIn is a function
    if (typeof signIn !== 'function') {
      setErrors({ general: 'Authentication service not available. Please try again.' });
      return;
    }

    setIsLoading(true);
    setErrors({});
    setResendStatus('');

    try {
      // Trim email and password before passing to Supabase
      await signIn(formData?.email?.trim(), formData?.password?.trim());
      navigate('/user-dashboard');
    } catch (error) {
      // Enhanced error logging for development diagnosis
      console.error('Login error:', error);
      
      // Get the raw error message
      let errorMessage = error?.message || 'Invalid email or password. Please try again.';
      
      // CRITICAL FIX: Remove any Google/OAuth references from email/password login errors
      // Strip out OAuth provider references that shouldn't appear in email login context
      errorMessage = // Clean up multiple spaces
      errorMessage?.replace(/google/gi, '')?.replace(/oauth/gi, '')?.replace(/provider/gi, '')?.replace(/\s{2,}/g, ' ')?.trim();
      
      // Handle specific error cases for email/password authentication
      if (errorMessage?.toLowerCase()?.includes('email not confirmed')) {
        errorMessage = 'Please check your email and confirm your account before logging in. Check your inbox (and spam folder) for the confirmation email.';
        setResendStatus('');
      } else if (errorMessage?.toLowerCase()?.includes('invalid login credentials') || 
                 errorMessage?.toLowerCase()?.includes('invalid') ||
                 errorMessage === '') {
        errorMessage = 'Invalid email or password. Please check your credentials and try again.';
      }
      
      setErrors({ 
        general: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!formData?.email?.trim()) {
      setResendStatus('Enter your email above first.');
      return;
    }
    setIsResending(true);
    setResendStatus('');
    try {
      await resendConfirmationEmail(formData?.email?.trim());
      setResendStatus('Confirmation email re-sent. Please check your inbox and spam folders.');
    } catch (error) {
      setResendStatus(error?.message || 'Unable to resend confirmation email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleForgotPassword = () => {
    navigate('/reset-password');
  };

  return (
    <div className="bg-card border border-border rounded-2xl shadow-lg p-8">
      <form onSubmit={handleSubmit} className="space-y-6">
        {errors?.general && (
          <div className="p-4 bg-error/10 border border-error rounded-lg flex items-start space-x-3">
            <Icon name="AlertCircle" size={20} color="var(--color-error)" className="flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-error font-medium mb-1">{errors?.general}</p>
              {errors?.general?.toLowerCase()?.includes('confirm your account') && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-2 sm:space-y-0">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleResendConfirmation}
                    loading={isResending}
                  >
                    Resend confirmation email
                  </Button>
                  {resendStatus && (
                    <span className="text-xs text-muted-foreground">{resendStatus}</span>
                  )}
                </div>
              )}
              {errors?.general?.includes('confirm your account') && (
                <p className="text-xs text-error/80 mt-2">
                  Didn't receive the email?{' '}
                  <button
                    type="button"
                    onClick={() => navigate('/user-registration')}
                    className="underline hover:text-error"
                  >
                    Try registering again
                  </button>
                </p>
              )}
              <button
                type="button"
                onClick={() => navigator?.clipboard?.writeText(errors?.general)}
                className="text-xs text-error/80 hover:text-error underline mt-1"
              >
                Copy error details
              </button>
            </div>
          </div>
        )}

        <Input
          type="email"
          name="email"
          label="Email Address"
          placeholder="your.email@example.com"
          value={formData?.email || ''}
          onChange={handleChange}
          error={errors?.email}
          required
          autoComplete="email"
        />

        <div className="relative">
          <Input
            type={showPassword ? 'text' : 'password'}
            name="password"
            label="Password"
            placeholder="Enter your password"
            value={formData?.password || ''}
            onChange={handleChange}
            error={errors?.password}
            required
            autoComplete="current-password"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-9 text-muted-foreground hover:text-foreground transition-colors duration-200"
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
          </button>
        </div>

        <div className="flex items-center justify-end">
          <button
            type="button"
            onClick={handleForgotPassword}
            className="text-sm font-medium text-primary hover:text-primary/80 transition-colors duration-200"
          >
            Forgot password?
          </button>
        </div>

        <Button
          type="submit"
          variant="default"
          size="lg"
          fullWidth
          loading={isLoading}
          iconName="LogIn"
          iconPosition="right"
        >
          {isLoading ? 'Signing in...' : 'Sign In'}
        </Button>

        <div className="text-center pt-4 border-t border-border">
          <p className="text-sm text-muted-foreground">
            Don't have an account?{' '}
            <button
              type="button"
              onClick={() => navigate('/user-registration')}
              className="font-medium text-primary hover:text-primary/80 transition-colors duration-200"
            >
              Create one now
            </button>
          </p>
        </div>

        <div className="pt-4 space-y-2">
          <p className="text-xs text-center text-muted-foreground font-medium">
            Demo Credentials for Testing
          </p>
          <div className="bg-muted/30 rounded-lg p-3 text-xs space-y-1">
            <p className="text-foreground">
              <span className="font-medium">Email:</span> john.entrepreneur@example.com
            </p>
            <p className="text-foreground">
              <span className="font-medium">Password:</span> securepass123
            </p>
          </div>
        </div>
      </form>
    </div>
  );
};

export default SimpleLoginForm;
